import React, { useRef, useImperativeHandle, useEffect, useState } from "react";
import SelectManualShopForm, { SelectManualShopFormInstance } from "../SelectManualShopForm";
import ShippingAddressSelect, {
    ShippingAddressSelectInstance,
} from "@/scenes/manualOrder/Add/components/ShippingAddressSelect";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import { ManualOrderItem } from "@/scenes/manualOrder/Add/components/OrderItemForm/type";
import { observer } from "mobx-react-lite";
import OrderItemFormStore from "@/scenes/manualOrder/Add/components/OrderItemForm/OrderItemFormStore";
import OrderItemForm from "@/scenes/manualOrder/Add/components/OrderItemForm";
import {
    ConsigneeInfoFormInstance,
    ConsigneeInfoFormValues,
} from "@/scenes/manualOrder/Add/components/ConsigneeInfoForm/type";
import ConsigneeInfoForm from "@/scenes/manualOrder/Add/components/ConsigneeInfoForm";

interface CreateManualOrderFormProps {
    privacyProtection: boolean;
}

export interface CreateManualOrderFormInstance {
    getFormValues(): Promise<{
        shopId: number;
        consignee: Omit<ConsigneeInfoFormValues, "area">;
        orderItems: ManualOrderItem[];
        shippingAddress?: ShippingAddressDTO;
    }>;

    setConsigneeInfoFormValues(data: Partial<ConsigneeInfoFormValues>): void;

    setOrderItemsValues: React.Dispatch<React.SetStateAction<ManualOrderItem[]>>;

    setShipperAddressId: React.Dispatch<React.SetStateAction<number>>;
}

const CreateManualOrderForm: React.ForwardRefRenderFunction<
    CreateManualOrderFormInstance,
    CreateManualOrderFormProps
> = function ({ privacyProtection }, ref) {
    const selectShopForm = useRef<SelectManualShopFormInstance>();
    const consigneeInfoForm = useRef<ConsigneeInfoFormInstance>();
    const shippingAddressRef = useRef<ShippingAddressSelectInstance>();

    const [orderItemFormStore] = useState(() => new OrderItemFormStore());

    async function getFormValues() {
        const { shopId } = await selectShopForm.current.validateFields();

        const consignee = await consigneeInfoForm.current.getFormValues();

        const orderItems = await orderItemFormStore.getSelectedOrderItems();

        const shippingAddressId = shippingAddressRef.current.shipperAddressId;
        const shippingAddress = shippingAddressRef.current.shippingAddresses.find(({ id }) => id === shippingAddressId);

        return {
            shopId,
            consignee,
            orderItems,
            shippingAddress,
        };
    }

    useImperativeHandle(ref, () => ({
        getFormValues,
        setConsigneeInfoFormValues: consigneeInfoForm.current.setFieldsValue,
        setOrderItemsValues: orderItemFormStore.setOrderItems,
        setShipperAddressId: shippingAddressRef.current.setShipperAddressId,
    }));

    useEffect(() => {
        const selectedKeys = orderItemFormStore.orderItems.map(({ key }) => key);
        orderItemFormStore.setSelectedRowKeys(selectedKeys);
    }, [orderItemFormStore.orderItems]);

    return (
        <div>
            <SelectManualShopForm ref={selectShopForm} />

            <div className="flex mt30">
                <div style={{ flex: "0 0 auto" }}>
                    <ConsigneeInfoForm hideSmartButton ref={consigneeInfoForm} privacyProtection={privacyProtection} />
                </div>

                <div className="f1 ml20">
                    <ShippingAddressSelect ref={shippingAddressRef} />

                    <OrderItemForm
                        store={orderItemFormStore}
                        hideDelete
                        hideHistoryAddedManualOrderItemList
                        hideCheckbox={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default observer(React.forwardRef(CreateManualOrderForm));
